import { useEffect, useRef, useState } from 'react';

export async function uploadFile(file, upload, onProgress) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10 * 60 * 1000);
  try {
    const body = new FormData();
    Object.entries(upload.fields).forEach(([key, value]) => body.append(key, value));
    body.append('file', file);
    onProgress(0);
    // Use the same presigned POST transport as BR uploads. An opaque response
    // is only a transfer attempt; the API must verify the stored bytes before
    // the UI can say Saved. This does not require changing bucket CORS rules.
    const response = await fetch(upload.url, { method: 'POST', mode: 'no-cors', body, signal: controller.signal });
    if (response.type !== 'opaque' && !response.ok) throw new Error('Upload failed.');
    onProgress(100);
  } catch {
    throw new Error('Upload interrupted. Choose the same file again to retry.');
  } finally { clearTimeout(timeout); }
}

export default function useEquipmentAttachments(client) {
  const [files, setFiles] = useState(() => client.getAttachments());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const active = useRef(false);
  useEffect(() => {
    const warn = event => { if (active.current) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);

  async function add(newFiles) {
    if (active.current) return;
    active.current = true;
    setBusy(true);
    setError('');
    try {
      for (const file of newFiles) {
        const prior = files.find(item => item.name === file.name && item.size === file.size && item.status !== 'uploaded');
        const id = prior?.id || crypto.randomUUID();
        setFiles(prev => [...prev.filter(item => item.id !== id), { id, name: file.name, size: file.size, type: file.type, status: 'pending', progress: 0 }]);
        try {
          const prepared = await client.attachments('prepare', { attachmentId: id, file: { name: file.name, size: file.size, type: file.type } });
          setFiles(prepared.attachments);
          if (prepared.upload) {
            await uploadFile(file, prepared.upload, progress => setFiles(prev => prev.map(item => item.id === id ? { ...item, progress } : item)));
            const result = await client.attachments('complete', { attachmentId: id });
            setFiles(result.attachments);
          }
        } catch (failure) {
          setFiles(prev => prev.map(item => item.id === id ? { ...item, error: failure.message } : item));
          throw failure;
        }
      }
    } catch (failure) { setError(`${failure.message} If you selected more files, choose the remaining files again.`); }
    finally { active.current = false; setBusy(false); }
  }

  async function remove(file) {
    if (active.current) return;
    active.current = true;
    setBusy(true);
    setError('');
    try {
      const result = await client.attachments('remove', { attachmentId: file.id });
      setFiles(result.attachments);
    } catch (failure) {
      if (failure.status === 404) setFiles(prev => prev.filter(item => item.id !== file.id));
      else setError(failure.message);
    } finally { active.current = false; setBusy(false); }
  }
  return { files, busy, error, add, remove, pending: files.some(file => file.status !== 'uploaded') };
}
